/* BÄR — Pop-up de suscripción.
   Cuándo aparece: una sola vez, después de `data-delay` segundos, al bajar el 60% de la página
   o (en escritorio) cuando el mouse sale por arriba de la ventana, lo que pase primero.
   No vuelve a aparecer por 14 días si lo cerraron, y nunca más si ya se suscribieron
   (por el pop-up o por el formulario del home). */
class BarCapture extends HTMLElement {
  static KEY = 'bar-capture';
  static SENT = 'bar-capture-sent';
  static SNOOZE_DAYS = 14;

  connectedCallback() {
    this.dialog = this.querySelector('[data-bar-capture-dialog]');
    if (!this.dialog) return;
    this.querySelectorAll('[data-bar-capture-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', () => this.snooze());

    const form = this.querySelector('form');
    // En fase de captura: así corre antes que el script del captcha de Shopify, que puede frenar el evento.
    if (form) form.addEventListener('submit', (event) => this.onSubmit(event, form), true);

    // Cualquier suscripción por el formulario del home también cuenta.
    document.addEventListener('submit', (event) => {
      const f = event.target;
      if (f !== form && f.querySelector && f.querySelector('input[name="form_type"][value="customer"]')) this.store('done');
    });

    // Volvemos de enviar el pop-up: mostrar el resultado (éxito o error).
    if (this.session(BarCapture.SENT)) {
      this.session(BarCapture.SENT, null);
      if (this.querySelector('[data-bar-capture-done]')) this.store('done');
      this.open();
      return;
    }

    if (this.shouldWait()) return;
    this.arm();
  }

  shouldWait() {
    const state = this.store();
    if (state === 'done') return true;
    const until = Number(state || 0);
    return until > Date.now();
  }

  arm() {
    const delay = Math.max(3, Number(this.dataset.delay) || 15) * 1000;
    const fire = () => this.maybeOpen();
    // la foto de la ventana se baja unos segundos después de cargar, para que ya esté al abrir
    const img = this.querySelector('[data-bar-capture-img]');
    if (img) window.setTimeout(() => { img.loading = 'eager'; }, 3000);
    this.timer = window.setTimeout(fire, delay);
    this.onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.6) fire();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.onLeave = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) fire();
      };
      document.addEventListener('mouseout', this.onLeave);
    }
  }

  disarm() {
    window.clearTimeout(this.timer);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
  }

  maybeOpen() {
    // No tapar otra ventana abierta (lista de espera, carrito, menú).
    if (document.querySelector('dialog[open]') || document.body.classList.contains('overflow-hidden')) return;
    this.disarm();
    this.open();
  }

  open() {
    if (this.dialog.open) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  close() {
    if (this.store() !== 'done') this.snooze();
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }

  snooze() {
    if (this.store() === 'done') return;
    this.store(String(Date.now() + BarCapture.SNOOZE_DAYS * 86400000));
  }

  onSubmit(event, form) {
    if (!form.reportValidity()) {
      event.preventDefault();
      return;
    }
    // El formulario de Shopify solo guarda el email: nombre y WhatsApp van también a la planilla.
    const url = this.dataset.webhookUrl;
    if (url) {
      const payload = {
        tipo: 'captacion',
        nombre: (this.querySelector('[data-bar-capture-name]') || {}).value || '',
        email: (this.querySelector('[data-bar-capture-email]') || {}).value || '',
        telefono: (this.querySelector('[data-bar-capture-phone]') || {}).value || '',
        pagina: window.location.pathname,
        idioma: document.documentElement.lang || 'es',
      };
      // Una sola vez por email: el captcha de Shopify puede volver a disparar el submit.
      if (payload.email && payload.email !== this.sentEmail) {
        this.sentEmail = payload.email;
        const body = JSON.stringify(payload);
        // keepalive: el pedido sale aunque la página navegue a Shopify enseguida. Sin cookies de Google
        // (credentials: 'omit'), que con varias cuentas abiertas hacen fallar al script.
        // Si el navegador no acepta fetch con keepalive, sendBeacon como respaldo.
        const beacon = () => {
          try {
            if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=utf-8' }));
          } catch (e) {
            /* la suscripción en Shopify sigue igual */
          }
        };
        try {
          fetch(url, {
            method: 'POST',
            mode: 'no-cors',
            keepalive: true,
            credentials: 'omit',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body,
          }).catch(() => {});
        } catch (e) {
          beacon();
        }
      }
    }
    this.session(BarCapture.SENT, '1');
    // Shopify protege este formulario con hCaptcha. Si pide un desafío visible, quedaría
    // debajo del modal (el <dialog> va en la capa superior): se cierra el modal para que se vea.
    // Al volver de la página de Shopify se reabre con el resultado (ver connectedCallback).
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }

  store(value) {
    try {
      if (value === undefined) return window.localStorage.getItem(BarCapture.KEY);
      window.localStorage.setItem(BarCapture.KEY, value);
    } catch (e) {
      return null;
    }
    return value;
  }

  session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
    return value;
  }
}

if (!customElements.get('bar-capture')) customElements.define('bar-capture', BarCapture);
